import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Typography,
  Icon,
} from '@material-ui/core';

import styles from './styles';

const BillingAndPricingFaq = ({
  classes,
  includeOrgFaq,
}) => (
  <div className={classes.container}>
    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          Is Pingum really free forever on the Free plan?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Yes. If you create an organization and never upgrade your plan, you
          will never be asked to pay for using Pingum.
        </Typography>
      </AccordionDetails>
    </Accordion>

    {
      includeOrgFaq
      && (
        <Accordion>
          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
            <Typography variant="subtitle1">
              Do I need to pay or provide a credit card to create an organization?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              Depending on the plan selected at organization creation,
              a card may be required.
              When creating a new organization under the free plan, you are not asked
              for a credit card and you do not need one to use the website.
              When selecting a paid plan at organization creation, a card is required.
              The organization will start with a 14 free trial. Once the trial ends,
              the card provided at org creation will be automatically charged for
              the first invoice.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )
    }

    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          How does Pingum&#39;s pricing work?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Pingum offers multiple pricing plans to allow you
          to pick the features and capabilities that best meet
          your organization&#39;s needs. Pingum does offer a
          free tier that does not require a credit card or
          payment. You can view the different plans on our <a href="https://pingum.io/pricing" target="_blank" rel="noopener noreferrer">pricing</a> page
          .  If you have additional questions or requests,
          please <a href="https://pingum.io/contact-us" target="_blank ">contact us</a>.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          What forms of payment do you accept?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          You can purchase Pingum with any major credit card.
          Monthly purchases must be paid for by credit card.
          Invoices payable by bank transfer or check can be made available
          if you pay annually on the pro or unlimited plans.
          Please <a href="/contact-us/">contact us</a> to learn more and request this option.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          We need to change the plan our organization is subscribed to.
          How will that work?
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography paragraph>
          You can upgrade or downgrade your plan at any time. Simply
          select the new plan from the billing page and save.
        </Typography>
        <Typography paragraph>
          If you are upgrading from the free plan to a paid plan, you will
          first need to provide a valid payment method before being allowed
          to upgrade. If you are upgrading for the first time, you will start
          with a free 14 day trial. After the trial ends, your card on file
          will be charged for the first invoice.
        </Typography>
        <Typography paragraph>
          If you are upgrading from a paid plan to a higher tier plan, you will
          receive  a prorated credit for the billing period on your current plan.
          Your payment method on file will then be charged for the remaining prorated
          amount based on the time left in the billing period and amount already paid
          in the previous plan.
        </Typography>
        <Typography paragraph>
          If you are downgrading your paid plan to a lesser tier plan, your
          Pingum account will receive a credit based on the amonut of time left
          in the current billing period.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          How does billing work for paid plans?
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography paragraph>
          Before an organization can be on a paid plan, you first must provide
          a valid credit or debit card. Once provided and after subscribing to
          a paid plan, you will be invoiced and charged for the amount
          corresponding with your selected plan. If a 14 free trial
          is applied to your plan, you will not be charged until after the
          trial completes.
        </Typography>
        <Typography paragraph>
          For organizations with paid plans, you can expect an invoice and charge
          at the start of each billing period, based on the payment amount corresponding
          with your selected plan. If choosing to pay annually, the combined amount of 12 months
          of usage will be charged at the start of the billing period (with a 10% discount).
        </Typography>
        <Typography paragraph>
          You can access all your past invoices and your incoming invoice
          from the invoices section of the billing page.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="subtitle1">
          What happens when I change from a Paid plan to the Free plan?
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography paragraph>
          You can always change from a paid plan to the free plan. In order
          to do this, you must first remove enough users from your organization
          so you only have 5 or fewer users and 2 or fewer shared dashboards remain.
          Once you have done this, you can select the free plan options from the
          billing page and change your subscription.
        </Typography>
        <Typography paragraph>
          Upon confirmation of the downgrade to free plan, a prorated credit
          will be applied to your Pingum account.
        </Typography>
      </AccordionDetails>
    </Accordion>

    {
      includeOrgFaq
      && (
        <Accordion>
          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
            <Typography variant="subtitle1">
              How do I cancel or deactivate my organization?
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }}>
            <Typography paragraph>
              To deactivate your organization, head to the billing page of your
              organization. In the Pingum Plan section there is a DEACTIVATE
              ORGANIZATION button. Simply click this button and confirm the action.
            </Typography>
            <Typography paragraph>
              If your organization is on a paid plan,
              you will be removed from that plan.
            </Typography>
            <Typography paragraph>
              In addition, paid plan organization will be
              given a prorated credit on their their Pingum account.
            </Typography>
            <Typography paragraph>
              All credit cards on file will be removed.
            </Typography>
            <Typography paragraph>
              No users will be able to access this organization from their
              organization page, or from the direct link to the organization.
            </Typography>
            <Typography paragraph>
              Access to the organization in the website, mobile application,
              and from the APIs will be restricted.
              To reactive the organization, you will need to <a href="/contact-us/">contact us</a>.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )
    }
  </div>
);

BillingAndPricingFaq.propTypes = {
  classes: PropTypes.object,
  includeOrgFaq: PropTypes.bool,
};

BillingAndPricingFaq.defaultProps = {
  classes: {},
  includeOrgFaq: false,
};

export default withStyles(styles)(BillingAndPricingFaq);
